import { useEffect, useRef } from "react";

interface RangeSliderProps {
  testId: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onValueChange(value: number): void;
  className?: string;
  disabled?: boolean;
}

/**
 * An uncontrolled range input with a native listener.
 *
 * The screenshot harness changes sliders by setting `el.value` and dispatching
 * `input`/`change`. React's synthetic onChange is suppressed by its internal
 * value tracker in that case, so the native listener is deliberate: it keeps
 * programmatic E2E changes working exactly like a drag.
 */
export function RangeSlider({
  testId,
  min,
  max,
  step,
  value,
  onValueChange,
  className = "slider",
  disabled = false,
}: RangeSliderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const changeRef = useRef(onValueChange);
  changeRef.current = onValueChange;

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const handle = (): void => {
      const next = Number(input.value);
      if (Number.isFinite(next)) changeRef.current(next);
    };

    input.addEventListener("input", handle);
    input.addEventListener("change", handle);
    return () => {
      input.removeEventListener("input", handle);
      input.removeEventListener("change", handle);
    };
  }, []);

  return (
    <input
      ref={inputRef}
      data-testid={testId}
      className={className}
      type="range"
      min={min}
      max={max}
      step={step}
      defaultValue={value}
      disabled={disabled}
    />
  );
}
